import { CdkDrag, CdkDragEnd, CdkDragHandle } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { VideoPlayerPlaceholderComponent } from '@c/video-hub/video-player-placeholder/video-player-placeholder.component';
import { VideoPlayerSkeletonComponent } from '@c/video-hub/video-player-skeleton/video-player-skeleton.component';
import { VimeoPlayerComponent } from '@c/video-hub/vimeo-player/vimeo-player.component';
import { YoutubePlayerComponent } from '@c/video-hub/youtube-player/youtube-player.component';
import { VideoHubTabNames, videoHubResetDiffSeconds } from '@constants/video-hub.contants';
import { BackgroundVideoResizableDirective } from '@core/directives/background-video-resize/background-video-resizable.directive';
import { SanitizeHtmlPipe } from '@core/pipes/sanitize-html.pipe';
import { ElementResizeOptions } from '@mod/video-hub/video-hub-resize-options.model';
import { LocalStorageService } from '@s/local-storage.service';
import { ObservableService } from '@s/observable.service';
import { VideoHubExternalUserDataService } from '@s/video-hub/video-hub-external-user-data.service';
import { VideoHubNavigationService } from '@s/video-hub/video-hub-navigation.service';
import { VideoHubService } from '@s/video-hub/video-hub.service';
import { VideoHubPlayer } from '@t/video-hub/video-hub-player.interface';
import { Subscription, from, interval, switchMap } from 'rxjs';
import { BackgroundVideoModalModel } from './background-video-modal.model';

@Component({
    selector: 'app-background-video-modal',
    imports: [
        YoutubePlayerComponent,
        VimeoPlayerComponent,
        MatIconModule,
        CdkDrag,
        CdkDragHandle,
        MatButtonModule,
        BackgroundVideoResizableDirective,
        MatProgressSpinnerModule,
        MatTooltipModule,
        SanitizeHtmlPipe,
        VideoPlayerPlaceholderComponent,
        VideoPlayerSkeletonComponent,
    ],
    templateUrl: './background-video-modal.component.html',
    styleUrl: './background-video-modal.component.scss'
})
export class BackgroundVideoModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostListener('window:beforeunload', ['$event'])
  async beforeUnloadHandler(): Promise<boolean> {
    await this.sendVideoExternalData();
    return await this.handleWindowUnload();
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.handleWindowResize();
  }

  @ViewChild('player')
  public videoPlayer: VideoHubPlayer;
  @ViewChild('drag')
  public drag: CdkDrag;
  protected videoMetadata = input.required<BackgroundVideoModalModel>();
  protected isMaskVisible = signal<boolean>(false);
  protected currentPositionAndSizeData = signal<{
    x: number;
    y: number;
    width: number;
    height: number;
  }>(null);
  protected elementResizeOptions: ElementResizeOptions = {
    aspectRatio: 16 / 9,
    maxHeight: Infinity,
    minHeight: 230,
    parentClass: '.background-video-pane',
  };
  protected isVideoLoaded = signal(false);
  protected isNeededShowPlaceholder = signal(false);

  private videoCurrentTime = toSignal(interval(1000).pipe(switchMap(() => from(this.videoPlayer.getCurrentTime()))));
  private subscription: Subscription = new Subscription();

  constructor(
    private videoHubService: VideoHubService,
    private localStorageService: LocalStorageService,
    private observableService: ObservableService,
    private videoHubExternalUserDataService: VideoHubExternalUserDataService,
    private videoHubNavigationService: VideoHubNavigationService,
  ) {}

  ngOnInit(): void {
    this.currentPositionAndSizeData.set({
      x: this.videoMetadata()?.position?.x ?? 50,
      y:
        this.videoMetadata()?.position?.y ??
        window.innerHeight - this.videoHubService.BackgroundVideoInitialHeight - 100,
      width: this.videoMetadata()?.size?.width ?? 230 * (16 / 9),
      height: this.videoMetadata()?.size?.height ?? 230,
    });

    this.subscription.add(
      this.observableService.onUserLogout.subscribe(() => {
        this.videoHubService.closeBackgroundVideo();
      }),
    );
  }

  ngAfterViewInit(): void {
    this.subscription.add(
      interval(this.videoHubExternalUserDataService.sendUpdateUserStopTimeSeconds * 1000).subscribe(() =>
        this.sendVideoExternalData(),
      ),
    );
  }

  public async close(): Promise<void> {
    await this.sendVideoExternalData();
    this.videoHubService.closeBackgroundVideo();
  }

  protected onResizeStart(): void {
    this.isMaskVisible.set(true);
    (document.querySelector('.background-video-backdrop') as HTMLElement).style.display = 'block';
  }

  protected onResizeEnd(): void {
    this.isMaskVisible.set(false);
    (document.querySelector('.background-video-backdrop') as HTMLElement).style.display = 'none';
  }

  public onResizing(data: { x: number; y: number; width: number; height: number }): void {
    this.currentPositionAndSizeData.set({
      ...data,
    });
  }

  protected async onPlayVideoAgain(): Promise<void> {
    await this.videoPlayer.setCurrentTime(0);
    await this.videoPlayer.play();
    this.isNeededShowPlaceholder.set(false);
  }

  public async expandVideo(): Promise<void> {
    await this.sendVideoExternalData();
    await this.videoHubNavigationService.redirectToVideoHubTab(
      VideoHubTabNames.VideoHubPlayer,
      [this.videoMetadata().video.id.toString()],
      { t: await this.getCurrentPlayerTime() },
    );
  }

  protected onDragStarted(): void {
    this.isMaskVisible.set(true);
    (document.querySelector('.background-video-backdrop') as HTMLElement).style.display = 'block';
  }

  protected onVideoEnd(): void {
    this.isNeededShowPlaceholder.set(true);
  }

  protected onDragEnded(event: CdkDragEnd): void {
    const rect = event.source.element.nativeElement.getBoundingClientRect();
    this.currentPositionAndSizeData.update((data) => ({
      ...data,
      x: rect.x,
      y: rect.y,
    }));
    this.isMaskVisible.set(false);
    (document.querySelector('.background-video-backdrop') as HTMLElement).style.display = 'none';
  }

  protected async openInSeparateWindow(): Promise<void> {
    this.pauseVideo();
    await this.sendVideoExternalData();
    const videoUrl = await this.videoPlayer.getVideoUrl();
    window.open(videoUrl, '_blank');
  }

  protected async getCurrentPlayerTime(): Promise<number> {
    return this.videoPlayer.getCurrentTime();
  }

  protected onVideoLoaded(): void {
    this.isVideoLoaded.set(true);
  }

  private pauseVideo(): void {
    this.videoPlayer.pause();
    this.sendVideoExternalData();
  }

  private handleWindowResize(): void {
    if (window.innerWidth < 991) {
      this.close();
    }
  }

  private async handleWindowUnload(): Promise<boolean> {
    this.localStorageService.set(
      this.videoHubService.BackgroundVideoLocalStorageKey,
      JSON.stringify({
        video: { ...this.videoMetadata().video },
        timestamp: await this.videoPlayer.getCurrentTime(),
        size: { width: this.currentPositionAndSizeData().width, height: this.currentPositionAndSizeData().height },
        position: { x: this.currentPositionAndSizeData().x, y: this.currentPositionAndSizeData().y },
      }),
    );

    return true;
  }

  private async sendVideoExternalData(): Promise<void> {
    const object = {
      videoId: this.videoMetadata().video.videoId,
      stopPosition:
        this.videoMetadata().video.durationSeconds - this.videoCurrentTime() > videoHubResetDiffSeconds
          ? this.videoCurrentTime()
          : 0,
      watched:
        this.videoMetadata().video.durationSeconds - this.videoCurrentTime() > videoHubResetDiffSeconds
          ? this.videoMetadata().video.externalUserData.watched
          : true,
    };
    return await this.videoHubExternalUserDataService.updateVideosExternalData(object);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
